import { createFileRoute, Link } from "@tanstack/react-router";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ChecklistDownloadForm } from "@/components/calv/ChecklistDownloadForm";
import { SITE_URL } from "@/lib/locale";
import { jsonLdScript, breadcrumbSchema } from "@/lib/seo";

const TITLE = "Performance-Marketing-Checkliste für KMU — Calvarius";
const DESC =
  "Strukturierte Setup-Sequenz in sieben Phasen — von der wirtschaftlichen Vorab-Klärung bis zum Reporting-Rhythmus. 20 Seiten, methodisch substantiell, kostenfrei.";
const URL_ = `${SITE_URL}/tools/performance-marketing-checkliste`;
const ALT = `${SITE_URL}/en/tools/performance-marketing-checklist`;

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "Performance-Marketing-Checkliste für KMU",
  description: DESC,
  author: { "@type": "Organization", name: "Calvarius" },
  publisher: { "@type": "Organization", name: "Calvarius" },
  inLanguage: "de-DE",
  url: URL_,
  about: "Performance-Marketing-Setup für mittelständische Unternehmen",
};

export const Route = createFileRoute("/tools/performance-marketing-checkliste")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL_ },
      { property: "og:locale", content: "de_DE" },
      { property: "og:locale:alternate", content: "en_US" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [
      { rel: "canonical", href: URL_ },
      { rel: "alternate", hrefLang: "de", href: URL_ },
      { rel: "alternate", hrefLang: "en", href: ALT },
      { rel: "alternate", hrefLang: "x-default", href: URL_ },
    ],
    scripts: [
      jsonLdScript(
        breadcrumbSchema([
          { name: "Start", path: "/" },
          { name: "Tools", path: "/tools" },
          { name: "Performance-Marketing-Checkliste", path: "/tools/performance-marketing-checkliste" },
        ]),
      ),
      jsonLdScript(articleSchema),
    ],
  }),
  component: ChecklisteDE,
});

const PHASES = [
  {
    n: "01",
    t: "Wirtschaftliche Vorab-Klärung",
    d: "AOV, Marge und Max-CPC-Formel. Wann Performance-Marketing nicht der richtige Hebel ist — und welche Vorab-Fragen die Entscheidung tragen.",
  },
  {
    n: "02",
    t: "Tracking- und Daten-Architektur",
    d: "Conversion-Tracking sauber aufsetzen: Server-Side, Consent-Mode v2, First-Party-Daten, Offline-Conversions. Datenbasis vor Kampagne.",
  },
  {
    n: "03",
    t: "Konto- und Kampagnen-Struktur",
    d: "Funnel-Phasen-Differenzierung, Konto-Ownership, Naming-Konventionen. Struktur, die Steuerung und spätere Übergabe erlaubt.",
  },
  {
    n: "04",
    t: "Pacing-Strategie und Budget-Architektur",
    d: "Tages- und Monats-Pacing, Budget-Reserven, saisonale Anpassungen. Budget als steuerbares Instrument, nicht als Wochenmittel.",
  },
  {
    n: "05",
    t: "Conversion-Optimierung-Hebel",
    d: "Landingpage-Architektur, Form-Optimierung, A/B-Test-Methodik, Mobile-First. Was vor jeder Budget-Erhöhung geprüft gehört.",
  },
  {
    n: "06",
    t: "Negative-Keyword-Management und Streuverlust-Reduktion",
    d: "Suchbegriff-Reports lesen, drei Typen von Negatives sauber anwenden. Disziplin, die monatliches Geld zurückholt.",
  },
  {
    n: "07",
    t: "Reporting-Rhythmus und Eskalations-Logik",
    d: "Kern-Metriken, Frequenzen, drei Eskalations-Stufen. Reporting, das Entscheidungen vorbereitet — kein Dashboard-Selbstzweck.",
  },
];

function ChecklisteDE() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          number="01"
          eyebrow="METHODISCHE CHECKLISTE · AUSGABE 2026"
          headline={<>Performance-Marketing-Setup <em>methodisch sauber.</em></>}
          lead="Strukturierte Setup-Sequenz in sieben Phasen — von der wirtschaftlichen Vorab-Klärung über Tracking-Architektur, Konto-Struktur und Pacing-Strategie bis hin zu Conversion-Hebeln, Negative-Keyword-Management und Reporting-Rhythmus. 20 Seiten, methodisch substantiell, kostenfrei."
          metaStrip={["7 Phasen · ~20 Seiten", "PDF · kostenfrei", "Substanz statt Tipps-Liste"]}
          breadcrumbs={[{ label: "Start", to: "/" }, { label: "Tools", to: "/tools" }, { label: "Performance-Marketing-Checkliste" }]}
        />

        <Section number="02" kicker="Inhalt" headline={<>Sieben Phasen, <em>methodisch geordnet.</em></>}>
          <ol className="space-y-4">
            {PHASES.map((p) => (
              <li key={p.n} className="flex gap-5 rounded-lg border border-rule bg-white p-5">
                <span className="font-mono text-xs tracking-wider text-ink-4">{p.n}</span>
                <div>
                  <h3 className="font-display text-lg text-ink">{p.t}</h3>
                  <p className="mt-1 text-[15px] leading-relaxed text-ink-2">{p.d}</p>
                </div>
              </li>
            ))}
          </ol>
        </Section>

        <Section number="03" kicker="Download" headline={<>Methodischer <em>Zugang</em> zur Checkliste.</>} variant="tinted">
          <ChecklistDownloadForm
            locale="de"
            checklistSlug="performance-marketing-setup"
            directDownloadUrl="/static/checklists/performance-marketing-setup-de.pdf"
          />
        </Section>

        <Section number="04" kicker="Methodischer Hinweis" headline={<>Checkliste ist <em>kein Ersatz</em> für individuelle Substanz.</>}>
          <div className="max-w-2xl rounded-lg border border-rule bg-bg-subtle p-7 text-[16px] leading-relaxed text-ink-2">
            <p>
              Die Checkliste ist substantiell, aber kein Ersatz für individuelle methodische Substanz. Jedes Unternehmen hat eigene Margen-Strukturen, AOV-Bandbreiten und Conversion-Mechaniken. Wenn Sie die Anwendung auf Ihren konkreten Fall besprechen wollen: kostenfreies Strategiegespräch unter{" "}
              <Link to="/kontakt" search={{ betreff: "strategiegespraech" }} className="text-cyan-deep underline decoration-cyan/40 underline-offset-4 hover:decoration-cyan-deep">
                /kontakt
              </Link>
              .
            </p>
          </div>
        </Section>

        <Section number="05" kicker="Verwandte Werkzeuge" headline={<>Drei methodische <em>Anschluss-Punkte.</em></>}>
          <div className="grid gap-5 md:grid-cols-3">
            <Link to="/tools/wirtschaftlichkeitsrechner" className="group rounded-lg border border-rule bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-cyan">
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">Tool</p>
              <h3 className="mt-3 font-display text-lg text-ink">Wirtschaftlichkeit vorab prüfen</h3>
              <p className="mt-2 text-[14px] leading-relaxed text-ink-2">CPC-Schwellen aus AOV, Marge, CVR. Vor der ersten Kampagne.</p>
              <span className="mt-4 inline-block font-mono text-xs uppercase tracking-wider text-ink-2 group-hover:text-cyan-deep">Rechner öffnen →</span>
            </Link>
            <Link to="/tools/lighthouse-check" className="group rounded-lg border border-rule bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-cyan">
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">Tool</p>
              <h3 className="mt-3 font-display text-lg text-ink">Lighthouse-Check Ihrer Site</h3>
              <p className="mt-2 text-[14px] leading-relaxed text-ink-2">Performance, Accessibility, Best Practices, SEO — in unter einer Minute.</p>
              <span className="mt-4 inline-block font-mono text-xs uppercase tracking-wider text-ink-2 group-hover:text-cyan-deep">Audit starten →</span>
            </Link>
            <Link to="/leistungen/performance-marketing" className="group rounded-lg border border-rule bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-cyan">
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-deep">Leistung</p>
              <h3 className="mt-3 font-display text-lg text-ink">Performance-Marketing-Leistungen</h3>
              <p className="mt-2 text-[14px] leading-relaxed text-ink-2">Wenn die Checkliste die Notwendigkeit bestätigt — methodische Umsetzung.</p>
              <span className="mt-4 inline-block font-mono text-xs uppercase tracking-wider text-ink-2 group-hover:text-cyan-deep">Leistungen ansehen →</span>
            </Link>
          </div>
        </Section>
      </main>
      <Footer />
    </>
  );
}
